import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  OverrideWindowShape,
  withOverrideWindowRules,
} from "./override-window";

// ─── Shared schemas ─────────────────────────────────────────────────────────

/** One override row (history-preserving — includes revoked / expired rows). */
export const InventoryPolicyOverrideRowSchema = z.object({
  id: z.string().uuid(),
  inventoryPolicyId: z.string().uuid(),
  minQuantity: z.number().nullable(),
  maxQuantity: z.number().nullable(),
  safetyStock: z.number().nullable(),
  minFloor: z.number().nullable(),
  hardMin: z.number().nullable(),
  hardMax: z.number().nullable(),
  orderMultiple: z.number().nullable(),
  autoTransferEligible: z.boolean().nullable(),
  autoBuyEligible: z.boolean().nullable(),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  /** Advisory review date; null on rows created before the rule existed. */
  reviewAt: z.string().nullable(),
  reason: z.string().nullable(),
  createdByUserId: z.string().uuid(),
  createdByUserName: z.string().nullable(),
  deactivatedAt: z.string().nullable(),
  deactivatedByUserId: z.string().uuid().nullable(),
  deactivatedByUserName: z.string().nullable(),
  /** The row this one replaced (set by "update"); null for a fresh override. */
  supersedesOverrideId: z.string().uuid().nullable(),
  /** True iff active right now (not deactivated, within [startsAt, endsAt)). */
  isActive: z.boolean(),
  /** True iff active and `reviewAt` has passed. */
  needsReview: z.boolean(),
  createdAt: z.string(),
});

export type InventoryPolicyOverrideRow = z.infer<
  typeof InventoryPolicyOverrideRowSchema
>;

/**
 * Target a policy by its natural key: the SKU at a warehouse
 * (docs/procurement/product-v3-only.md). ProductV3 has no variant dimension, so
 * the SKU is the whole product identity.
 */
const PolicyTargetShape = {
  warehouseId: z.string().uuid(),
  /** ProductV3 key (the SKU). */
  productV3Key: z.string().min(1),
} as const;

/** The resolved effective policy (base ⊕ active overlay) for display. */
export const EffectivePolicySchema = z.object({
  policyId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  /** ProductV3 key (the SKU): the policy's product identity. */
  productV3Key: z.string().nullable(),
  minQuantity: z.number(),
  maxQuantity: z.number(),
  autoTransferEligible: z.boolean(),
  autoBuyEligible: z.boolean(),
  baseMinQuantity: z.number(),
  baseMaxQuantity: z.number(),
  baseAutoTransferEligible: z.boolean(),
  baseAutoBuyEligible: z.boolean(),
  // Effective forecast levels. A safety-stock override shifts ROP and S by
  // the same buffer delta; the base fields preserve the engine output.
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  baseSafetyStock: z.number().nullable(),
  baseReorderPoint: z.number().nullable(),
  baseOrderUpToLevel: z.number().nullable(),
  minFloor: z.number().nullable(),
  hardMin: z.number().nullable(),
  hardMax: z.number().nullable(),
  /** Active pack/rounding override; null = the vendor master MOQ/increment. */
  orderMultiple: z.number().nullable(),
  effectiveTriggerLevel: z.number(),
  effectiveLevelSource: z.enum([
    "forecast_safety_stock_override",
    "forecast_calculated",
    "min_override",
    "fixed_min",
    "min_calculated",
  ]),
  minFloorApplied: z.boolean(),
  activeOverrideCount: z.number(),
});

export type EffectivePolicy = z.infer<typeof EffectivePolicySchema>;

// ─── Create ─────────────────────────────────────────────────────────────────

/** The overridable fields. Omit / null = "this override doesn't touch it". */
const OverrideFieldsShape = {
  minQuantity: z.number().nonnegative().nullable().optional(),
  maxQuantity: z.number().nonnegative().nullable().optional(),
  safetyStock: z.number().nonnegative().nullable().optional(),
  minFloor: z.number().nonnegative().nullable().optional(),
  hardMin: z.number().nonnegative().nullable().optional(),
  hardMax: z.number().nonnegative().nullable().optional(),
  /**
   * Pack / rounding quantity for buys at this SKU×warehouse. Replaces the
   * vendor master MOQ + increment, so it may be above or below the carton.
   */
  orderMultiple: z.number().positive().nullable().optional(),
  autoTransferEligible: z.boolean().nullable().optional(),
  autoBuyEligible: z.boolean().nullable().optional(),
} as const;

interface OverrideFieldsInput {
  minQuantity?: number | null;
  maxQuantity?: number | null;
  safetyStock?: number | null;
  minFloor?: number | null;
  hardMin?: number | null;
  hardMax?: number | null;
  orderMultiple?: number | null;
  autoTransferEligible?: boolean | null;
  autoBuyEligible?: boolean | null;
}

const setsAField = {
  check: (v: OverrideFieldsInput) =>
    v.minQuantity != null ||
    v.maxQuantity != null ||
    v.safetyStock != null ||
    v.minFloor != null ||
    v.hardMin != null ||
    v.hardMax != null ||
    v.orderMultiple != null ||
    v.autoTransferEligible != null ||
    v.autoBuyEligible != null,
  message: "An override must set at least one field.",
} as const;

const maxNotBelowMin = {
  check: (v: OverrideFieldsInput) =>
    v.minQuantity == null ||
    v.maxQuantity == null ||
    v.maxQuantity >= v.minQuantity,
  message: "maxQuantity must be >= minQuantity.",
} as const;

const hardStopsOrdered = {
  check: (v: OverrideFieldsInput) =>
    v.hardMin == null || v.hardMax == null || v.hardMin <= v.hardMax,
  message: "hardMin must be <= hardMax.",
} as const;

// The window carries the shared expiry-or-review rule: a new override must
// end or come up for review (docs/procurement/lead-time-overrides.md).
export const CreateInventoryPolicyOverrideInputSchema = withOverrideWindowRules(
  z
    .object({
      // Target policy by its natural key (matches how the board addresses a line).
      ...PolicyTargetShape,
      ...OverrideFieldsShape,
      ...OverrideWindowShape,
      reason: z.string().max(1000).nullable().optional(),
    })
    .refine(setsAField.check, { message: setsAField.message })
    .refine(maxNotBelowMin.check, { message: maxNotBelowMin.message })
    .refine(hardStopsOrdered.check, { message: hardStopsOrdered.message }),
);

export const CreateInventoryPolicyOverrideResponseSchema = z.object({
  override: InventoryPolicyOverrideRowSchema,
  effective: EffectivePolicySchema,
});

export type CreateInventoryPolicyOverrideInput = z.infer<
  typeof CreateInventoryPolicyOverrideInputSchema
>;
export type CreateInventoryPolicyOverrideResponse = z.infer<
  typeof CreateInventoryPolicyOverrideResponseSchema
>;

// ─── List by policy ──────────────────────────────────────────────────────────

export const ListInventoryPolicyOverridesInputSchema =
  z.object(PolicyTargetShape);

export const ListInventoryPolicyOverridesResponseSchema = z.object({
  policyId: z.string().uuid().nullable(),
  effective: EffectivePolicySchema.nullable(),
  overrides: z.array(InventoryPolicyOverrideRowSchema),
});

export type ListInventoryPolicyOverridesInput = z.infer<
  typeof ListInventoryPolicyOverridesInputSchema
>;
export type ListInventoryPolicyOverridesResponse = z.infer<
  typeof ListInventoryPolicyOverridesResponseSchema
>;

// ─── List by product (one row per warehouse×variant policy combo) ────────────

/** One policy combo for a product — effective + base values for the table. */
export const PolicyOverrideComboRowSchema = z.object({
  policyId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  divisionName: z.string().nullable(),
  /** ProductV3 key (the SKU) — echo it back so the row can address its policy. */
  productV3Key: z.string().nullable(),
  /**
   * NULL on every v3-era policy (ProductV3 has no variants). This was a required
   * uuid, and the service filtered null-variant rows out to satisfy it — which
   * silently dropped every v3 policy and made this endpoint return zero rows.
   */
  productVariantId: z.string().uuid().nullable(),
  variantLabel: z.string().nullable(),
  minQuantity: z.number(),
  maxQuantity: z.number(),
  autoTransferEligible: z.boolean(),
  autoBuyEligible: z.boolean(),
  baseMinQuantity: z.number(),
  baseMaxQuantity: z.number(),
  baseAutoTransferEligible: z.boolean(),
  baseAutoBuyEligible: z.boolean(),
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  baseSafetyStock: z.number().nullable(),
  baseReorderPoint: z.number().nullable(),
  baseOrderUpToLevel: z.number().nullable(),
  minFloor: z.number().nullable(),
  hardMin: z.number().nullable(),
  hardMax: z.number().nullable(),
  /** Active pack/rounding override; null = the vendor master MOQ/increment. */
  orderMultiple: z.number().nullable(),
  effectiveTriggerLevel: z.number(),
  effectiveLevelSource: EffectivePolicySchema.shape.effectiveLevelSource,
  minFloorApplied: z.boolean(),
  activeOverrideCount: z.number(),
});

export type PolicyOverrideComboRow = z.infer<
  typeof PolicyOverrideComboRowSchema
>;

export const ListPolicyOverrideCombosInputSchema = z.object({
  /** ProductV3 key (the SKU). */
  productV3Key: z.string().min(1),
});

export const ListPolicyOverrideCombosResponseSchema = z.object({
  rows: z.array(PolicyOverrideComboRowSchema),
});

export type ListPolicyOverrideCombosInput = z.infer<
  typeof ListPolicyOverrideCombosInputSchema
>;
export type ListPolicyOverrideCombosResponse = z.infer<
  typeof ListPolicyOverrideCombosResponseSchema
>;

// ─── Update (revoke + create replacement) ───────────────────────────────────

export const UpdateInventoryPolicyOverrideInputSchema = withOverrideWindowRules(
  z
    .object({
      overrideId: z.string().uuid(),
      ...OverrideFieldsShape,
      ...OverrideWindowShape,
      reason: z.string().max(1000).nullable().optional(),
    })
    .refine(setsAField.check, { message: setsAField.message })
    .refine(maxNotBelowMin.check, { message: maxNotBelowMin.message })
    .refine(hardStopsOrdered.check, { message: hardStopsOrdered.message }),
);

export const UpdateInventoryPolicyOverrideResponseSchema = z.object({
  /** The replacement row (now active). */
  override: InventoryPolicyOverrideRowSchema,
  /** The row it replaced, stamped as revoked by the editor. */
  superseded: InventoryPolicyOverrideRowSchema,
  effective: EffectivePolicySchema,
});

export type UpdateInventoryPolicyOverrideInput = z.infer<
  typeof UpdateInventoryPolicyOverrideInputSchema
>;
export type UpdateInventoryPolicyOverrideResponse = z.infer<
  typeof UpdateInventoryPolicyOverrideResponseSchema
>;

// ─── Revoke ──────────────────────────────────────────────────────────────────

export const RevokeInventoryPolicyOverrideInputSchema = z.object({
  overrideId: z.string().uuid(),
});

export const RevokeInventoryPolicyOverrideResponseSchema = z.object({
  override: InventoryPolicyOverrideRowSchema,
});

export type RevokeInventoryPolicyOverrideInput = z.infer<
  typeof RevokeInventoryPolicyOverrideInputSchema
>;
export type RevokeInventoryPolicyOverrideResponse = z.infer<
  typeof RevokeInventoryPolicyOverrideResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const inventoryPolicyOverridesContracts = {
  create: oc
    .input(CreateInventoryPolicyOverrideInputSchema)
    .output(CreateInventoryPolicyOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Override an inventory policy for a SKU",
      description:
        "Creates a time-windowed override of min, max, safety stock, min floor, order multiple or auto-buy/transfer eligibility for a SKU at a warehouse; returns the new effective policy.",
    }),
  listByPolicy: oc
    .input(ListInventoryPolicyOverridesInputSchema)
    .output(ListInventoryPolicyOverridesResponseSchema)
    .route({
      method: "GET",
      summary: "List inventory policy overrides for a SKU",
      description:
        "For a SKU at one warehouse, returns active and historical policy overrides plus the resolved effective min, max, safety stock and reorder levels.",
    }),
  listByProduct: oc
    .input(ListPolicyOverrideCombosInputSchema)
    .output(ListPolicyOverrideCombosResponseSchema)
    .route({
      method: "GET",
      summary: "List a product's inventory policies by warehouse",
      description:
        "For one SKU, returns each warehouse's inventory policy with effective and base min, max, safety stock, reorder levels, eligibility and active override count.",
    }),
  update: oc
    .input(UpdateInventoryPolicyOverrideInputSchema)
    .output(UpdateInventoryPolicyOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Update an inventory policy override",
      description:
        "Edits an active policy override by revoking it and creating a linked replacement in one transaction; returns both rows and the new effective policy.",
    }),
  revoke: oc
    .input(RevokeInventoryPolicyOverrideInputSchema)
    .output(RevokeInventoryPolicyOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Revoke an inventory policy override",
      description:
        "Ends a policy override immediately, recording who revoked it; the base policy or any earlier override applies again.",
    }),
} as const;
